import React, { useState, useEffect } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Button,
  Snackbar
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { db } from '../../../firebase'; // Assicurati di configurare Firebase
import { doc, setDoc, getDocs, collection } from 'firebase/firestore';
import '../../../styles/AdministratorTabPage.scss';

const AdministratorTabPage = () => {
  // Ora l'oggetto ha una proprietà fieldsets che è un array di "fieldset"
  const [administratorData, setAdministratorData] = useState({
    fieldsets: [],
  });
  const [originalAdministratorData, setOriginalAdministratorData] = useState({
    fieldsets: [],
  });
  
  const [hasChanges, setHasChanges] = useState(false);

  // Dialog per il refresh
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  // Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [undoAction, setUndoAction] = useState(null);

  // Possibili colori tra cui scegliere
  const colors = [
    { name: 'Rosso', value: '#f44336' },
    { name: 'Verde', value: '#4caf50' },
    { name: 'Blu', value: '#2196f3' },
    { name: 'Arancio', value: '#ff983b' },
  ];

  // Al mount, recupera i dati da Firestore
  useEffect(() => {
    const fetchAdminSettings = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'adminSettings'));
        let adminSettings = { fieldsets: [] };
        
        // Supponiamo di avere un solo doc "global" o simile
        snapshot.forEach(doc => {
          adminSettings = doc.data();
        });

        // Se il documento non contiene fieldsets, inizializziamo l'array
        if (!adminSettings.fieldsets) {
          adminSettings.fieldsets = [];
        }

        setAdministratorData(adminSettings);
        setOriginalAdministratorData(adminSettings);
      } catch (error) {
        console.error('Error fetching menu items:', error);
      }
    };

    fetchAdminSettings();
  }, []);

  // Quando cambia un messaggio
  const handleMessageChange = (event, index) => {
    const value = event.target.value;
    setAdministratorData((prev) => {
      const newFieldsets = [...prev.fieldsets];
      newFieldsets[index].message = value;
      return { ...prev, fieldsets: newFieldsets };
    });
    setHasChanges(true);
  };

  // Quando cambia il colore
  const handleColorChange = (event, index) => {
    const color = event.target.value;
    setAdministratorData((prev) => {
      const newFieldsets = [...prev.fieldsets];
      newFieldsets[index].messageColor = color;
      return { ...prev, fieldsets: newFieldsets };
    });
    setHasChanges(true);
  };

  // Aggiunge un nuovo fieldset con valori di default
  const handleAddFieldset = () => {
    setAdministratorData((prev) => ({
      ...prev,
      fieldsets: [
        ...prev.fieldsets,
        { message: '', messageColor: '#f44336' },
      ],
    }));
    setHasChanges(true);
  };

  // Rimuove il fieldset in posizione index
  const handleRemoveFieldset = (index) => {
    setAdministratorData((prev) => {
      const newFieldsets = prev.fieldsets.filter((_, i) => i !== index);
      return { ...prev, fieldsets: newFieldsets };
    });
    setHasChanges(true);
  };

  // Ripristina lo stato originale
  const handleClear = () => {
    setAdministratorData(originalAdministratorData);
    setHasChanges(false);
  };

  // Salvataggio su Firestore
  const handleSave = async () => {
    try {
      const adminSettingsRef = doc(db, 'adminSettings', 'global');
      await setDoc(adminSettingsRef, administratorData);
      showSnackbar('Impostazioni salvate con successo');
      setHasChanges(false);
    } catch (error) {
      showSnackbar('Errore durante il salvataggio');
    }
  };

  // Apertura/chiusura dialog di conferma "refresh"
  const handleOpenRefreshDialog = () => {
    setOpenCancelDialog(true);
  };

  const handleCloseRefreshDialog = () => {
    setOpenCancelDialog(false);
  };

  // Snackbar
  const showSnackbar = (message, action = null) => {
    setSnackbarMessage(message);
    setUndoAction(action);
    setOpenSnackbar(true);
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  return (
    <>
      {/* Pulsanti in alto a destra */}
      <div className="main-cta-container">
        <div></div>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!hasChanges}
          startIcon={<SaveIcon />}
        >
          Salva
        </Button>
        <Button
          onClick={handleOpenRefreshDialog}
          disabled={!hasChanges}
          variant="contained"
        >
          <RefreshIcon />
        </Button>
      </div>

      {/* Contenitore principale */}
      <div className="administrator-tab-container">
        <div className="administrator-tab-fieldset">
            <h3>Amministrazione</h3>
        </div>
        {administratorData.fieldsets.map((fieldset, index) => (
          <div key={index} className="administrator-tab-fieldset">
            <div className="administrator-tab-fieldset-container">
              {/* Campo "Messaggio" */}
              <div className="administrator-tab-field message">
                <TextField
                  label="Messaggio"
                  fullWidth
                  value={fieldset.message}
                  onChange={(e) => handleMessageChange(e, index)}
                  margin="normal"
                />
              </div>

              {/* Campo "Colore" */}
              <div className="administrator-tab-field message-color">
                <FormControl fullWidth margin="normal">
                  <InputLabel id={`color-select-label-${index}`}>Colore</InputLabel>
                  <Select
                    labelId={`color-select-label-${index}`}
                    value={fieldset.messageColor}
                    onChange={(e) => handleColorChange(e, index)}
                    renderValue={(value) => {
                      const c = colors.find((col) => col.value === value);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: value,
                              border: '1px solid #ccc',
                              borderRadius: '50%',
                            }}
                          />
                          {c ? c.name : ''}
                        </Box>
                      );
                    }}
                  >
                    {colors.map((color) => (
                      <MenuItem key={color.value} value={color.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              backgroundColor: color.value,
                              border: '1px solid #ccc',
                              borderRadius: '50%',
                            }}
                          />
                          {color.name}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
                {/* Pulsante per rimuovere il fieldset */}
                <Button
                variant="outlined"
                color="error"
                onClick={() => handleRemoveFieldset(index)}
                >
                Elimina messaggio
                </Button>
            </div>

          </div>
        ))}

        {/* Pulsante per aggiungere un nuovo fieldset */}
        <Button
          onClick={handleAddFieldset}
          variant="contained"
          style={{ margin: '1rem 0', background: '#FAA916', color: 'black', fontWeight: '600' }}
        >
          Aggiungi Messagio
        </Button>

        {/* Snackbar */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          action={
            undoAction && (
              <Button color="secondary" size="small" onClick={undoAction}>
                Undo
              </Button>
            )
          }
        />
      </div>

      {/* Refresh Dialog */}
      <Dialog
        open={openCancelDialog}
        onClose={handleCloseRefreshDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Sei sicuro di voler ricaricare? Le modifiche apportate verranno perse.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleClear();
              handleCloseRefreshDialog();
            }}
            className="confirm-btn"
            autoFocus
          >
            Conferma
          </Button>
          <Button onClick={handleCloseRefreshDialog} className="cancel-btn">
            Annulla
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdministratorTabPage;
